import { Badge, Button } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  useCampagnes,
  useListesNewsletter,
  useReglagesNewsletter,
  type CampagneNewsletter,
} from '../../api/newsletter.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleCampagneDialog } from './NouvelleCampagneDialog.js';
import { formaterTaux, tauxMoyen, tauxSur } from './taux.js';
import styles from './NewsletterPage.module.css';
/** Tableau de bord de la newsletter : où en sont les lettres, à qui elles partent, que faire ensuite. */
export function VueEnsembleTab({ onOuvrir }: { onOuvrir: (onglet: string) => void }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const campagnes = useCampagnes(galerieSlug);
  const listes = useListesNewsletter(galerieSlug);
  const reglages = useReglagesNewsletter(galerieSlug);
  const [nouvelle, setNouvelle] = useState(false);
  const ecriture = galerieALaction(galerie, 'newsletter:write');
  const toutes = campagnes.data?.campagnes ?? [];
  const envoyees = toutes.filter((c) => c.etat === 'envoyee');
  const enCours = toutes.filter((c) => c.etat === 'programmee');
  const brouillons = toutes.filter((c) => c.etat === 'brouillon');
  const dernieres = toutes.slice(0, 5);
  const listesActives = (listes.data?.listes ?? []).filter((l) => !l.archive);
  const date = (v: string, avecHeure = false) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      ...(avecHeure ? { timeStyle: 'short' as const } : {}),
    }).format(new Date(v));
  const lien = (c: CampagneNewsletter) => `/${galerieSlug}/newsletter/campagnes/${c.id}`;
  const chargement = campagnes.isPending || listes.isPending;
  if (chargement) return <p>{t('commun:chargement')}</p>;
  if (campagnes.isError || listes.isError) return <p role="alert">{t('commun:erreurReseau')}</p>;
  const premiersPas = !listesActives.length || !toutes.length;
  return (
    <section className={styles.section} data-ui="newsletter-overview">
      {reglages.data && !reglages.data.reglages.connecteur.configure ? (
        <div className={styles.alerte} role="status">
          <p>{t('regie:newsletter.vue.brevoAbsent')}</p>
          <Button variant="secondary" size="sm" onClick={() => onOuvrir('reglages')}>
            {t('regie:newsletter.vue.voirReglages')}
          </Button>
        </div>
      ) : null}

      {premiersPas ? (
        <article className={styles.carte} data-ui="newsletter-first-steps">
          <h2>{t('regie:newsletter.vue.premiersPas.titre')}</h2>
          <p>{t('regie:newsletter.vue.premiersPas.intro')}</p>
          <ol className={styles.pasPedagogiques}>
            <li data-fait={listesActives.length ? 'oui' : undefined}>
              <strong>{t('regie:newsletter.vue.premiersPas.liste')}</strong>
              <span>{t('regie:newsletter.vue.premiersPas.listeAide')}</span>
              {!listesActives.length && ecriture ? (
                <Button variant="secondary" size="sm" onClick={() => onOuvrir('listes')}>
                  {t('regie:newsletter.listes.nouvelle')}
                </Button>
              ) : null}
            </li>
            <li data-fait={toutes.length ? 'oui' : undefined}>
              <strong>{t('regie:newsletter.vue.premiersPas.campagne')}</strong>
              <span>{t('regie:newsletter.vue.premiersPas.campagneAide')}</span>
              {!toutes.length && ecriture ? (
                <Button size="sm" onClick={() => setNouvelle(true)}>
                  {t('regie:newsletter.campagnes.nouvelle')}
                </Button>
              ) : null}
            </li>
            <li>
              <strong>{t('regie:newsletter.vue.premiersPas.envoi')}</strong>
              <span>{t('regie:newsletter.vue.premiersPas.envoiAide')}</span>
            </li>
          </ol>
        </article>
      ) : (
        <div className={styles.actions}>
          {ecriture ? (
            <Button onClick={() => setNouvelle(true)}>
              {t('regie:newsletter.campagnes.nouvelle')}
            </Button>
          ) : null}
        </div>
      )}

      <dl className={styles.chiffres} data-ui="newsletter-figures">
        {(
          [
            ['envoyees', String(envoyees.length)],
            ['ouvertures', formaterTaux(tauxMoyen(envoyees, 'ouvertures'))],
            ['clics', formaterTaux(tauxMoyen(envoyees, 'clics'))],
            ['listes', String(listesActives.length)],
          ] as const
        ).map(([cle, valeur]) => (
          <div key={cle} className={styles.carte}>
            <dt>{t(`regie:newsletter.vue.chiffres.${cle}`)}</dt>
            <dd className={styles.chiffre}>{valeur}</dd>
          </div>
        ))}
      </dl>

      <div className={styles.grille}>
        <article className={styles.carte}>
          <h2>{t('regie:newsletter.vue.enCours.titre')}</h2>
          {enCours.length ? (
            <ul className={styles.liste}>
              {enCours.map((c) => (
                <li key={c.id}>
                  <Link to={lien(c)}>{c.titre}</Link>
                  <span className={styles.sousTitre}>
                    {c.envoiDemandeLe
                      ? t('regie:newsletter.vue.enCours.demandeLe', {
                          date: date(c.envoiDemandeLe, true),
                        })
                      : t('regie:newsletter.vue.enCours.sansDate')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.vide}>{t('regie:newsletter.vue.enCours.vide')}</p>
          )}
          <p className={styles.aide}>{t('regie:newsletter.vue.enCours.aide')}</p>
        </article>
        <article className={styles.carte}>
          <h2>{t('regie:newsletter.vue.brouillons.titre')}</h2>
          {brouillons.length ? (
            <ul className={styles.liste}>
              {brouillons.slice(0, 4).map((c) => (
                <li key={c.id}>
                  <Link to={lien(c)}>{c.titre}</Link>
                  <span className={styles.sousTitre}>
                    {t('regie:newsletter.vue.brouillons.modifieLe', { date: date(c.modifieLe) })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.vide}>{t('regie:newsletter.vue.brouillons.vide')}</p>
          )}
        </article>
      </div>

      <article className={styles.carte}>
        <h2>{t('regie:newsletter.vue.dernieres.titre')}</h2>
        {dernieres.length ? (
          <div className={styles.tableau}>
            <table>
              <thead>
                <tr>
                  {['titre', 'etat', 'envoyeeLe', 'ouvertures', 'clics'].map((c) => (
                    <th
                      scope="col"
                      key={c}
                      className={c === 'clics' || c === 'envoyeeLe' ? styles.secondaire : undefined}
                    >
                      {t(`regie:newsletter.campagnes.colonnes.${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dernieres.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link to={lien(c)}>{c.titre}</Link>
                    </td>
                    <td>
                      <Badge
                        tone={
                          c.etat === 'envoyee'
                            ? 'available'
                            : c.etat === 'echouee'
                              ? 'sold'
                              : 'neutral'
                        }
                      >
                        {t(`regie:newsletter.etats.${c.etat}`)}
                      </Badge>
                    </td>
                    <td className={styles.secondaire}>{c.envoyeeLe ? date(c.envoyeeLe) : '—'}</td>
                    <td className={styles.chiffre}>
                      {formaterTaux(
                        c.statistiques
                          ? tauxSur(c.statistiques.ouvertures, c.statistiques.delivres)
                          : null,
                      )}
                    </td>
                    <td className={`${styles.chiffre} ${styles.secondaire}`}>
                      {formaterTaux(
                        c.statistiques
                          ? tauxSur(c.statistiques.clics, c.statistiques.delivres)
                          : null,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={styles.vide}>{t('regie:newsletter.vue.dernieres.vide')}</p>
        )}
        {toutes.length > dernieres.length ? (
          <Button variant="ghost" size="sm" onClick={() => onOuvrir('campagnes')}>
            {t('regie:newsletter.vue.dernieres.toutes', { count: toutes.length })}
          </Button>
        ) : null}
      </article>

      <article className={styles.carte}>
        <h2>{t('regie:newsletter.vue.tailles.titre')}</h2>
        {listesActives.length ? (
          <ul className={styles.liste}>
            {listesActives.map((l) => (
              <li key={l.id} className={styles.ligneListe}>
                <span>{l.nom}</span>
                <span className={styles.chiffre}>
                  {t('regie:newsletter.listes.destinataires', { count: l.membres })}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.vide}>{t('regie:newsletter.vue.tailles.vide')}</p>
        )}
        <Button variant="ghost" size="sm" onClick={() => onOuvrir('listes')}>
          {t('regie:newsletter.vue.tailles.gerer')}
        </Button>
      </article>
      <NouvelleCampagneDialog open={nouvelle} onOpenChange={setNouvelle} />
    </section>
  );
}
